// SETTINGS V2 — nameplate row detail (B7-FE1, plan §6A.3 affordances 2 + 3).
// Split out of NameplateRowV2 to keep both files well under 300 lines.
//
// Renders exactly two things: the ID link with a copy button, and the QR with a
// PNG download. The switch (affordance 1) stays on the row itself.
//
// The QR encodes the server-echoed `idLink` (https://{resolver}/m/{CODE}). Since
// D7-FE1 (owner ruling D7, 2026-08-11) this is the machine's ONE code —
// MachineQRCodeDialog encodes the same link, no longer the kiosk URL, so the two
// surfaces cannot produce two different stickers for one machine. The client
// never assembles the link (plan §4) — it is displayed and encoded verbatim.
//
// Inline styles + scoped CSS-var tokens. No MUI, no dt tokens, no Tailwind.
import React, { useEffect, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { useBreakpoint } from "../../../../shared/hooks/useBreakpoint";
import Loader from "../../../../shared/components/Loader";
import { downloadFile } from "../../../../shared/utils/fileUtils";
import type { NameplateStatus } from "../../../machinery/models/nameplateModels";

/** The QR spec requires a 4-module quiet zone; without it scanners fail. */
const QR_MARGIN_MODULES = 4;

export interface NameplateRowDetailV2Props {
  machineId: string;
  internalNumber: string;
  status?: NameplateStatus;
  loading: boolean;
}

const NameplateRowDetailV2: React.FC<NameplateRowDetailV2Props> = ({
  machineId,
  internalNumber,
  status,
  loading,
}) => {
  const { translate: t } = useTranslation("settings");
  const { isPhone } = useBreakpoint();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const id = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(id);
  }, [copied]);

  const canvasId = `sv2-nameplate-qr-${machineId}`;
  const idLink = status?.idLink || "";

  const handleCopy = async () => {
    if (!idLink) return;
    try {
      await navigator.clipboard.writeText(idLink);
      setCopied(true);
    } catch {
      // Clipboard blocked (insecure context / permission). The link is on screen
      // and selectable, so this stays silent by design.
    }
  };

  const handleDownload = () => {
    const canvas = document.getElementById(canvasId) as HTMLCanvasElement | null;
    if (!canvas) return;
    const png = canvas.toDataURL("image/png").replace("image/png", "image/octet-stream");
    downloadFile(png, `nameplate-${internalNumber}-qr.png`);
  };

  const label: React.CSSProperties = {
    fontFamily: "var(--font-mono)",
    fontSize: 9.5,
    textTransform: "uppercase",
    letterSpacing: "0.09em",
    color: "var(--faint)",
  };

  const shell = (children: React.ReactNode) => (
    <div
      style={{
        display: "flex",
        flexDirection: isPhone ? "column" : "row",
        alignItems: isPhone ? "stretch" : "flex-start",
        gap: isPhone ? 14 : 20,
        padding: isPhone ? "12px 14px" : "14px 18px",
        marginLeft: isPhone ? 0 : 44,
        background: "var(--card-2)",
        border: "1px solid var(--border-soft)",
        borderRadius: 10,
        minWidth: 0,
        boxSizing: "border-box",
      }}
    >
      {children}
    </div>
  );

  if (loading && !status) {
    return shell(
      <div style={{ flex: 1, display: "grid", placeItems: "center", padding: "18px 0" }}>
        <Loader />
      </div>
    );
  }

  // No active code yet — the code is minted atomically on the first enable, so
  // there is genuinely nothing to show. Say so instead of rendering an empty box.
  if (!idLink) {
    return shell(
      <div style={{ fontSize: 12.5, color: "var(--muted)", lineHeight: 1.6 }}>
        {t("settings:nameplates.noCodeYet")}
      </div>
    );
  }

  // NO regenerate / "new code" action here — owner ruling 2026-08-11 (B7 plan §6A.1 D3): it invalidates every printed plate and triage was unanimous it would only be pressed by accident. Endpoint exists server-side for genuine compromise, deliberately UI-less. Do not add without a new owner ruling.
  return shell(
    <>
      {/* Affordance 2 — the ID link + copy. */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={label}>{t("settings:nameplates.idLinkLabel")}</div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
            minWidth: 0,
          }}
        >
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 12,
              color: "var(--ink)",
              overflowWrap: "anywhere",
              userSelect: "all",
              flex: "1 1 200px",
              minWidth: 0,
            }}
          >
            {idLink}
          </span>
          <button
            type="button"
            className="btn"
            onClick={handleCopy}
            style={{ flexShrink: 0 }}
          >
            {copied ? t("settings:nameplates.copied") : t("settings:nameplates.copyLink")}
          </button>
        </div>
        <div style={{ fontSize: 11.5, color: "var(--muted)", lineHeight: 1.55 }}>
          {t("settings:nameplates.idLinkHint")}
        </div>

        {/* NO regenerate / "new code" action here — owner ruling 2026-08-11 (B7 plan §6A.1 D3): it invalidates every printed plate and triage was unanimous it would only be pressed by accident. Endpoint exists server-side for genuine compromise, deliberately UI-less. Do not add without a new owner ruling. */}
      </div>

      {/* Affordance 3 — the QR (encodes idLink) + PNG download. */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 8,
          flexShrink: 0,
        }}
      >
        <div
          style={{
            background: "#FFFFFF",
            border: "1px solid var(--border)",
            borderRadius: 10,
            padding: 8,
            lineHeight: 0,
          }}
        >
          <QRCodeCanvas
            id={canvasId}
            value={idLink}
            size={isPhone ? 148 : 132}
            level="M"
            marginSize={QR_MARGIN_MODULES}
            bgColor="#FFFFFF"
            fgColor="#000000"
            style={{ display: "block", maxWidth: "100%", height: "auto" }}
          />
        </div>
        <button type="button" className="btn" onClick={handleDownload}>
          {t("settings:nameplates.downloadQr")}
        </button>
      </div>
    </>
  );
};

export default NameplateRowDetailV2;
